import React from 'react';
import { Trans } from '@lingui/macro';
import { Flex, Chip, Group, Box } from '@landr/maestro';
import { KeyQuality, KeyRootApi } from 'types/generated/graphql';
import { cva } from '@landr/styled-system/css';
import { KeyToggle } from './KeyToggle';
import { sharpsAndFlatsContainerStyles } from './styled';

const chipStyles = cva({
    base: {
        cursor: 'click',
        borderColor: 'border.high',
        '&[data-state="checked"]': {
            backgroundColor: 'surface.inverted',
            color: 'text.inverted.high',
        },
        _hover: {
            backgroundColor: 'surface.neutral.high.hover',
            color: 'text.inverted.high',
        },
    },
});

type Notes = [KeyRootApi] | [KeyRootApi, KeyRootApi];
type KeyToggle = {
    value: KeyRootApi;
    notes: Notes;
};

const SharpAndFlatKeys: KeyToggle[] = [
    {
        value: KeyRootApi.CSharpDFlat,
        notes: [KeyRootApi.C, KeyRootApi.D],
    },
    {
        value: KeyRootApi.DSharpEFlat,
        notes: [KeyRootApi.D, KeyRootApi.E],
    },
    {
        value: KeyRootApi.FSharpGFlat,
        notes: [KeyRootApi.F, KeyRootApi.G],
    },
    {
        value: KeyRootApi.GSharpAFlat,
        notes: [KeyRootApi.G, KeyRootApi.A],
    },
    {
        value: KeyRootApi.ASharpBFlat,
        notes: [KeyRootApi.A, KeyRootApi.B],
    },
];

const Keys: KeyToggle[] = [
    {
        value: KeyRootApi.C,
        notes: [KeyRootApi.C],
    },
    {
        value: KeyRootApi.D,
        notes: [KeyRootApi.D],
    },
    {
        value: KeyRootApi.E,
        notes: [KeyRootApi.E],
    },
    {
        value: KeyRootApi.F,
        notes: [KeyRootApi.F],
    },
    {
        value: KeyRootApi.G,
        notes: [KeyRootApi.G],
    },
    {
        value: KeyRootApi.A,
        notes: [KeyRootApi.A],
    },
    {
        value: KeyRootApi.B,
        notes: [KeyRootApi.B],
    },
];

interface KeysFilterProps {
    keyrootnote: string | null;
    keyquality: string | null;
    toggleKeyRootFilter: (value: KeyRootApi) => void;
    toggleKeyQualityFilter: (value: KeyQuality) => void;
    isMobile?: boolean;
}

export const KeysFilter: React.FC<KeysFilterProps> = ({
    keyrootnote,
    keyquality,
    toggleKeyRootFilter,
    toggleKeyQualityFilter,
    isMobile,
}) => {
    return (
        <Group width="100%" direction="vertical">
            <Group.Item>
                <Box
                    maxWidth={isMobile ? '100%' : '288px'}
                    marginY="none"
                    marginX="auto"
                    justifyContent="center"
                    display="flex"
                    flexWrap="wrap"
                >
                    <Flex mb="xs" gap="6px">
                        {SharpAndFlatKeys.map((key, index) => {
                            return (
                                <Box
                                    key={key.value}
                                    className={sharpsAndFlatsContainerStyles({
                                        index: index as 0 | 1,
                                    })}
                                >
                                    <KeyToggle
                                        value={key.value}
                                        notes={key.notes}
                                        isSelected={key.value === keyrootnote}
                                        onChange={toggleKeyRootFilter}
                                        isMobile={!!isMobile}
                                    />
                                </Box>
                            );
                        })}
                    </Flex>
                    <Flex gap="6px">
                        {Keys.map((key) => {
                            return (
                                <KeyToggle
                                    key={key.value}
                                    value={key.value}
                                    notes={key.notes}
                                    isSelected={key.value === keyrootnote}
                                    onChange={toggleKeyRootFilter}
                                    isMobile={!!isMobile}
                                />
                            );
                        })}
                    </Flex>
                </Box>
            </Group.Item>
            <Group.Item>
                <Flex mt="sm">
                    <Chip
                        mr="sm"
                        flexGrow={1}
                        isChecked={keyquality === KeyQuality.Major}
                        onClick={() => toggleKeyQualityFilter(KeyQuality.Major)}
                        className={chipStyles()}
                        cursor="pointer"
                    >
                        <Trans>Major</Trans>
                    </Chip>
                    <Chip
                        flexGrow={1}
                        isChecked={keyquality === KeyQuality.Minor}
                        onClick={() => toggleKeyQualityFilter(KeyQuality.Minor)}
                        palette="subtle"
                        className={chipStyles()}
                        cursor="pointer"
                    >
                        <Trans>Minor</Trans>
                    </Chip>
                </Flex>
            </Group.Item>
        </Group>
    );
};
